// ./src/components/features/EventPartners/LinkedContactSection/LinkedContactSection.tsx
//
// When a partner is linked to a company (venue/vendor) or a person, show that
// record's contact details READ-ONLY (the directory is the single source of
// truth) with an "Edit" button that opens the record's own editor — so enriching
// the website / socials updates it everywhere, not just for this event.

import React, { useState, useEffect, useCallback } from "react";
import { Paper, Stack, Group, Text, Anchor, Button, Box, Avatar } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { DEFAULT_PAGE_SIZE } from "@/config";
import { Venue, VenueService, Person, PersonService, getPersonDisplayName, VENUE_KIND, WHOLE_SET } from "@/utils/api";
import { personAvatarUrl } from "@/utils";
import { PersonsProvider, VenuesProvider, VenueCategoriesProvider } from "@/contexts";
import { AppIcon } from "@/components/generic";
import { useDrawerMount } from "@/hooks/useDrawerMount";
import { ContactEditDrawer } from "@/components/features/Contacts/ContactEditDrawer/ContactEditDrawer";
import { VenueEditDrawer } from "@/components/features/Venues/VenueEditDrawer/VenueEditDrawer";

export interface LinkedContactSectionProps {
  kind: "company" | "person";
  id: string;
  canEdit?: boolean;
  /**
   * Show the record's NAME + AVATAR (Gravatar fallback for a person) as the card header. Default true.
   * Set false where the caller already renders the name right next to the card (e.g. a clickable holder
   * name that expands it) so the name isn't shown twice — the card then keeps the generic title.
   */
  showIdentity?: boolean;
  /** Fires after the linked record is edited, so the parent can refresh (e.g. display name). */
  onChanged?: () => void;
  /** Fires when the internal edit drawer opens (true) / closes (false) — lets a parent drawer
   *  disable its own keyboard shortcuts while the nested contact editor is open. */
  onEditingChange?: (open: boolean) => void;
}

interface Detail {
  label: string;
  value: string;
  href?: string;
}

const DetailRow: React.FC<Detail> = ({ label, value, href }) => (
  <Group gap={6} justify="space-between" wrap="nowrap">
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    {href ? (
      <Anchor href={href} target="_blank" rel="noreferrer" size="sm" lineClamp={1}>
        {value}
      </Anchor>
    ) : (
      <Text size="sm" lineClamp={1}>
        {value}
      </Text>
    )}
  </Group>
);

export const LinkedContactSection: React.FC<LinkedContactSectionProps> = ({ kind, id, canEdit = true, showIdentity = true, onChanged, onEditingChange }) => {
  const { t } = useTranslation(["partners", "common"]);

  const [venue, setVenue] = useState<Venue | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [editing, setEditing] = useState(false);
  // Mounted while editing (and while it slides out), opened one commit after it mounted — so it slides in.
  const editDrawer = useDrawerMount(editing);

  // Let a parent (e.g. the guest drawer) know the nested contact editor is open, so it can pause its
  // own keyboard shortcuts while the user edits the contact.
  useEffect(() => {
    onEditingChange?.(editing);
  }, [editing, onEditingChange]);

  const load = useCallback(async () => {
    if (kind === "company") {
      const result = await VenueService.get(id);
      if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
        setVenue((result.data as Venue[])[0] ?? null);
      }
    } else {
      const result = await PersonService.get(id);
      if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
        setPerson((result.data as Person[])[0] ?? null);
      }
    }
  }, [kind, id]);

  useEffect(() => {
    setVenue(null);
    setPerson(null);
    load();
  }, [load]);

  const handleSaved = useCallback(() => {
    setEditing(false);
    load();
    onChanged?.();
  }, [load, onChanged]);

  const details: Detail[] = [];

  if (kind === "company" && venue) {
    if (venue.website) {
      details.push({ label: t("partners:linked.website", { defaultValue: "Website" }), value: venue.website, href: venue.website });
    }
    if (venue.email) {
      details.push({ label: t("partners:linked.email", { defaultValue: "Email" }), value: venue.email, href: `mailto:${venue.email}` });
    }
    if (venue.phone) {
      details.push({ label: t("partners:linked.phone", { defaultValue: "Phone" }), value: venue.phone, href: `tel:${venue.phone}` });
    }
  }

  if (kind === "person" && person) {
    if (person.personalWebsite) {
      details.push({ label: t("partners:linked.website", { defaultValue: "Website" }), value: person.personalWebsite, href: person.personalWebsite });
    }
    if (person.email) {
      details.push({ label: t("partners:linked.email", { defaultValue: "Email" }), value: person.email, href: `mailto:${person.email}` });
    }
    if (person.phone) {
      details.push({ label: t("partners:linked.phone", { defaultValue: "Phone" }), value: person.phone, href: `tel:${person.phone}` });
    }
    [person.socialUrl1, person.socialUrl2, person.socialUrl3].filter(Boolean).forEach((url, i) => {
      details.push({ label: t("partners:linked.social", { defaultValue: "Social {{n}}", n: i + 1 }), value: url as string, href: url as string });
    });
  }

  // The linked record's display name + avatar (a person's uploaded avatar, else the server-computed
  // Gravatar fallback; a company shows an initials avatar). Only shown when `showIdentity` and the
  // record has loaded — otherwise the card keeps its generic "… (from the directory)" title.
  const displayName = kind === "person" ? (person ? getPersonDisplayName(person) : "") : (venue?.name ?? "");
  const avatarUrl = kind === "person" ? personAvatarUrl(person) : null;
  const initials =
    displayName
      .trim()
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || null;
  const identityShown = showIdentity && (!!displayName || !!avatarUrl);

  const editButton = canEdit ? (
    <Button
      variant="subtle"
      size="compact-sm"
      leftSection={<AppIcon iconName="Edit" size={14} />}
      onClick={() => setEditing(true)}
    >
      {t("common:buttons.edit", { defaultValue: "Edit" })}
    </Button>
  ) : null;

  return (
    <>
      <Paper withBorder radius="md" p="sm" bg="var(--mantine-color-gray-0)">
        <Group justify="space-between" wrap="nowrap" align="flex-start" mb={details.length > 0 ? 8 : 0}>
          {identityShown ? (
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <Avatar src={avatarUrl} radius="xl" size="md">
                {initials}
              </Avatar>
              <div style={{ minWidth: 0 }}>
                <Text size="sm" fw={600} lineClamp={1}>
                  {displayName || t("partners:linked.aContact", { defaultValue: "A contact" })}
                </Text>
                <Text size="xs" c="dimmed">
                  {t("partners:linked.fromDirectory", { defaultValue: "From the directory" })}
                </Text>
              </div>
            </Group>
          ) : (
            <Text size="sm" fw={600}>
              {kind === "company"
                ? t("partners:linked.companyTitle", { defaultValue: "Company details (from the directory)" })
                : t("partners:linked.personTitle", { defaultValue: "Contact details (from the directory)" })}
            </Text>
          )}
          {editButton}
        </Group>

        {details.length > 0 ? (
          <Stack gap={4}>
            {details.map((d, i) => (
              <DetailRow key={i} {...d} />
            ))}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            {t("partners:linked.empty", { defaultValue: "No contact details yet — add a website, email or socials." })}
          </Text>
        )}
      </Paper>

      {editDrawer.mounted && kind === "person" && (
        <PersonsProvider pageSize={DEFAULT_PAGE_SIZE} autoLoad={false}>
          <ContactEditDrawer opened={editDrawer.opened} onClose={() => setEditing(false)} recordId={id} onUpdated={handleSaved} />
        </PersonsProvider>
      )}

      {editDrawer.mounted && kind === "company" && (
        <VenuesProvider pageSize={WHOLE_SET} autoLoad={false}>
          <VenueCategoriesProvider>
            <Box>
              <VenueEditDrawer opened={editDrawer.opened} onClose={() => setEditing(false)} recordId={id} kind={VENUE_KIND.VENDOR} onUpdated={handleSaved} />
            </Box>
          </VenueCategoriesProvider>
        </VenuesProvider>
      )}
    </>
  );
};
